import { createSelector } from 'reselect';
import { isUndefined } from 'lodash';
import {
    MODEL, PRACTICE, PASSAGE, COMPREHENSION,
} from '../constants/assessmentItemType';
import { DISCONTINUE_RULE } from '../constants/measure';

const RCT_DISCONTINUE_RULE = DISCONTINUE_RULE.RCT;

export const getIsRCTFluencyDataFetched = (state) => state.isDataFetched;

export const getIsRCTComprehensionDataFetched = (state) => state.isDataFetched;

export const getIsQuestionVisible = (state) => state.isQuestionVisible;

export const getIsSegmentVisible = (state) => state.isSegmentVisible;

export const getIsReadyScriptPlayed = (state) => state.isReadyScriptPlayed;

export const getActiveSegmentIndex = (state) => state.activeSegmentIndex;

export const getPassages = (state) => state.passages;

export const getTotalFluCompPassagesCount = createSelector(
    getPassages,
    (passages) => passages.length,
);

const getActivePassageQuestions = (state, pageType, activePassageIndex) => {
    const passages = getPassages(state);

    if (passages && !isUndefined(activePassageIndex)
            && passages[activePassageIndex]) {
        switch (pageType) {
        case PASSAGE:
            return passages[activePassageIndex].questions;
        case COMPREHENSION:
            return passages[activePassageIndex].comprehensionItems;
        default:
            return undefined;
        }
    }

    return undefined;
};

const getActivePassageSegments = (state, activePassageIndex) => {
    const passages = getPassages(state);
    if (passages && !isUndefined(activePassageIndex)) {
        return passages[activePassageIndex]
            ? passages[activePassageIndex].segments
            : undefined;
    }

    return undefined;
};

const getActivePassageTitle = (state, activePassageIndex) => {
    const passages = getPassages(state);
    if (passages && !isUndefined(activePassageIndex)) {
        return passages[activePassageIndex]
            ? passages[activePassageIndex].title
            : undefined;
    }

    return undefined;
};

export const getRCTFluCompItems = (state, pageType, activePassageIndex) => {
    switch (pageType) {
    case MODEL:
        return state.model.questions;
    case PRACTICE:
        return state.practice.questions;
    case PASSAGE:
    case COMPREHENSION:
        return getActivePassageQuestions(state, pageType, activePassageIndex);
    default:
        return undefined;
    }
};

export const getSegments = (state, pageType = PASSAGE, activePassageIndex) => {
    switch (pageType) {
    case MODEL:
        return state.model.segments;
    case PRACTICE:
        return state.practice.segments;
    case PASSAGE:
    case COMPREHENSION:
        return getActivePassageSegments(state, activePassageIndex);
    default:
        return undefined;
    }
};

export const getPassagesCount = (state, pageType = PASSAGE) => {
    if (!state.totalItems) {
        return undefined;
    }

    switch (pageType) {
    case MODEL:
        return state.totalItems.model;
    case PRACTICE:
        return state.totalItems.practice;
    case PASSAGE:
        return state.totalItems.passages;
    default:
        return undefined;
    }
};

export const getPassageTitle = (state, pageType = PASSAGE, activePassageIndex) => {
    switch (pageType) {
    case MODEL:
        return state.model.title;
    case PRACTICE:
        return state.practice.title;
    case PASSAGE:
    case COMPREHENSION:
        return getActivePassageTitle(state, activePassageIndex);
    default:
        return undefined;
    }
};

export const hasNextSegment = (state, pageType, activePassageIndex) => {
    const segments = getSegments(state, pageType, activePassageIndex);
    const activeSegmentIndex = getActiveSegmentIndex(state);

    return segments && segments[activeSegmentIndex + 1] !== undefined;
};

export const getCurrentQuestion = (state, pageType, activePassageIndex) => {
    // TODO: now it has the current question
    // Comprehension doesn't have the concept of current question
    if (pageType === COMPREHENSION) {
        return undefined;
    }

    const questions = getRCTFluCompItems(state, pageType, activePassageIndex);
    const activeSegmentIndex = getActiveSegmentIndex(state);

    if (questions) {
        return questions.find((question) => question.id === activeSegmentIndex);
    }

    return undefined;
};

export const hasQuestion = (state, pageType, activePassageIndex) => getCurrentQuestion(state, pageType, activePassageIndex) !== undefined;

export const getComprehensionQuestionCount = (state, activePassageIndex) => {
    const comprehensionItems = getRCTFluCompItems(state, COMPREHENSION, activePassageIndex);
    if (comprehensionItems) {
        return comprehensionItems.length;
    }

    return 0;
};

export const getSelectedComprehensionResponses = (state) => state.selectedResponses;

export const getQuestionIndex = (state) => state.questionIndex;

export const getFluCompFormId = (state) => state.formId;

export const isDiscontinueSegmentIndexRule = createSelector(
    [getActiveSegmentIndex],
    (segmentIndex) => segmentIndex < RCT_DISCONTINUE_RULE.segmentIndex,
);

export const isDiscontinueActivePassageRule = (_, activePassageIndex) => activePassageIndex < RCT_DISCONTINUE_RULE.passageIndex;
